"use client";

import { useCallback, useEffect, useState } from "react";
export function useUser({
  authClient
}) {
  const [isUpdating, setIsUpdating] = useState(false);
  const [user, setUser] = useState(null);
  useEffect(() => {
    async function loadUser() {
      const session = await authClient.getSession();
      setUser(session.data?.user ?? null);
    }
    loadUser();
  }, [authClient]);
  const updateProfile = useCallback(async (firstName, lastName) => {
    setIsUpdating(true);
    try {
      const {
        error
      } = await authClient.updateUser({
        name: `${firstName} ${lastName}`
      });
      if (error) {
        return {
          success: false,
          message: error.message ?? "Ett tekniskt fel uppstod"
        };
      }
      return {
        success: true,
        message: "Dina profiluppgifter har sparats."
      };
    } catch (err) {
      console.error("Failed to update profile:", err);
      return {
        success: false,
        message: "Ett tekniskt fel uppstod. Försök igen om en stund."
      };
    } finally {
      setIsUpdating(false);
    }
  }, [authClient]);
  const updatePassword = useCallback(async (currentPassword, newPassword) => {
    setIsUpdating(true);
    try {
      if (newPassword === currentPassword) {
        return {
          success: false,
          message: "Du måste välja ett nytt lösenord som skiljer sig från ditt nuvarande."
        };
      }
      const {
        error: error_0
      } = await authClient.changePassword({
        newPassword,
        currentPassword,
        revokeOtherSessions: true
      });
      if (error_0) {
        if (error_0.code === "INVALID_PASSWORD") {
          return {
            success: false,
            message: "Ditt nuvarande lösenord är felaktigt. Kontrollera och försök igen."
          };
        }
        return {
          success: false,
          message: error_0.message ?? "Ett tekniskt fel uppstod"
        };
      }
      return {
        success: true,
        message: "Ditt lösenord har uppdaterats."
      };
    } catch (err_0) {
      console.error("Failed to update password:", err_0);
      return {
        success: false,
        message: "Ett tekniskt fel uppstod. Försök igen om en stund."
      };
    } finally {
      setIsUpdating(false);
    }
  }, [authClient]);
  const updateEmail = useCallback(async (newEmail, password) => {
    setIsUpdating(true);
    try {
      // First verify the current password
      const {
        error: passwordError
      } = await authClient.changePassword({
        newPassword: password,
        currentPassword: password,
        revokeOtherSessions: false
      });
      if (passwordError) {
        if (passwordError.code === "INVALID_PASSWORD") {
          return {
            success: false,
            message: "Lösenordet är felaktigt. Kontrollera och försök igen."
          };
        }
        return {
          success: false,
          message: passwordError.message ?? "Ett tekniskt fel uppstod"
        };
      }

      // Now proceed with email change
      const {
        error: error_1
      } = await authClient.changeEmail({
        newEmail,
        callbackURL: window.location.origin
      });
      if (error_1) {
        if (error_1.code === "EMAIL_IS_THE_SAME") {
          return {
            success: false,
            message: "E-postadressen är samma som din nuvarande."
          };
        }
        return {
          success: false,
          message: error_1.message ?? "Ett tekniskt fel uppstod"
        };
      }
      return {
        success: true,
        message: "Vi har skickat en länk till din nuvarande e-postadress för att godkänna ändringen. När du har godkänt den skickar vi en verifieringslänk till den nya adressen, och ändringen slutförs när du har verifierat den."
      };
    } catch (err_1) {
      console.error("Failed to update email:", err_1);
      return {
        success: false,
        message: "Ett tekniskt fel uppstod. Försök igen om en stund."
      };
    } finally {
      setIsUpdating(false);
    }
  }, [authClient]);
  const deleteAccount = useCallback(async () => {
    setIsUpdating(true);
    try {
      const {
        error: error_2
      } = await authClient.deleteUser();
      if (error_2) {
        return {
          success: false,
          message: error_2.message ?? "Ett tekniskt fel uppstod"
        };
      }
      return {
        success: true,
        message: "Ditt konto har raderats."
      };
    } catch (err_2) {
      console.error("Failed to delete account:", err_2);
      return {
        success: false,
        message: "Ett tekniskt fel uppstod. Försök igen om en stund."
      };
    } finally {
      setIsUpdating(false);
    }
  }, [authClient]);
  return {
    user,
    updateProfile,
    updatePassword,
    updateEmail,
    deleteAccount,
    isUpdating
  };
}